import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getAllPositionHistory,
	getPositionHistory,
	getPositionMetrics,
	getPositionPoints,
	getTrackingVehicles,
	getTrip,
} from "../services";
import type {
	PaginatedResponse,
	PaginationParams,
	PositionHistoryItem,
	PositionMetricsParams,
	PositionMetricsResponse,
	RouteCoordinate,
	TrackingVehiclesResponse,
	Trip,
	TripParams,
} from "../types";

export function useVehicleTrips(
	vehicleId: number,
	params?: TripParams,
	enabled?: boolean
) {
	return useQuery<PaginatedResponse<Trip>>({
		queryKey: ["trip", vehicleId, params],
		queryFn: () => getTrip(vehicleId, params),
		enabled: enabled !== undefined ? enabled : !!vehicleId && vehicleId > 0,
		staleTime: 1000 * 60 * 5,
		refetchOnMount: "always",
	});
}

export function useVehicleRoutePoints(id: number) {
	return useQuery<RouteCoordinate[]>({
		queryKey: ["position-points", id],
		queryFn: () => getPositionPoints(id),
		enabled: !!id,
		staleTime: 1000 * 60 * 5,
	});
}

export function useVehiclePositionHistory(
	vehicleId: number,
	params?: PaginationParams,
	enabled?: boolean
) {
	return useQuery<PaginatedResponse<PositionHistoryItem>>({
		queryKey: ["position-history", vehicleId, params],
		queryFn: () => getPositionHistory(vehicleId, params),
		enabled: enabled !== undefined ? enabled : !!vehicleId,
		staleTime: 1000 * 60 * 3,
		refetchOnMount: "always",
	});
}

export function useAllPositionHistory(
	vehicleId: number,
	InitialDate?: string,
	FinalDate?: string,
	enabled?: boolean
) {
	return useQuery<RouteCoordinate[]>({
		queryKey: ["all-position-history", vehicleId, InitialDate, FinalDate],
		queryFn: () => getAllPositionHistory(vehicleId, InitialDate, FinalDate),
		enabled: enabled !== undefined ? enabled : !!vehicleId && vehicleId > 0,
		staleTime: 1000 * 60 * 3,
	});
}

export function usePositionMetrics(
	vehicleId: number,
	params?: PositionMetricsParams,
	enabled?: boolean
) {
	return useQuery<PositionMetricsResponse>({
		queryKey: ["position-metrics", vehicleId, params],
		queryFn: () =>
			getPositionMetrics(vehicleId, params as PositionMetricsParams),
		enabled:
			(enabled !== undefined ? enabled : !!vehicleId && vehicleId > 0) &&
			!!params,
		staleTime: 1000 * 60 * 3,
		refetchOnMount: "always",
	});
}

export function useTrackingVehicles() {
	return useQuery<TrackingVehiclesResponse>({
		queryKey: ["tracking-vehicles"],
		queryFn: () => getTrackingVehicles(),
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
	});
}

export function useRefreshPositionPoints() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: number }) => getPositionPoints(id),
		onSuccess: (data, variables) => {
			queryClient.setQueryData(["position-points", variables.id], data);
		},
	});
}
